import RegularText from '@/components/RegularText';
import { SETTING_TYPE, TypeDelete } from '@/constants/enum';
import { PATH } from '@/constants/path';
import { isEmpty } from '@/helpers';
import { IListLocation } from '@/types/components/location';
import { Badge, Box, Button, IndexTable, InlineStack, Tooltip } from '@shopify/polaris';
import { DeleteIcon, DuplicateIcon, EditIcon, NoteAddIcon } from '@shopify/polaris-icons';
import React, { Fragment } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

type TTableDelivery = {
  listLocations: IListLocation[];
  setTypeDelete: React.Dispatch<React.SetStateAction<TypeDelete>>;
  selectedView: number;
  handleShowModalDeleteLocation: (id: number) => void;
  handleShowModalDuplicateZone: (id: number) => void;
};

const TableDelivery = ({
  listLocations,
  setTypeDelete,
  selectedView,
  handleShowModalDeleteLocation,
  handleShowModalDuplicateZone,
}: TTableDelivery) => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const columnHeadings = [
    { title: 'Delivery zones', id: 'column-header--deliveryZones' },
    {
      title: 'Local delivery',
      id: 'column-header--localDelivery',
      hidden: false,
    },
    {
      title: 'Action',
      id: 'column-header--action',
      alignment: 'end',
    },
  ];

  const handleShowDetailLocation = (id: number) => {
    navigate({ pathname: `${PATH.LOCATIONS.pathname}/${id}`, search: searchParams.toString() });
  };

  const handleEditDeliverySettings = (id: number) => {
    navigate({ pathname: `${PATH.DELIVERY.pathname}/${id}`, search: searchParams.toString() });
  };

  const handleAddDeliverySettings = (zoneId: number) => {
    navigate({ pathname: `${PATH.DELIVERY.pathname}/new/${zoneId}`, search: searchParams.toString() });
  };

  const rowMarkup = listLocations.map((zone, index) => {
    return (
      <Fragment key={index}>
        <IndexTable.Row rowType="data" id={`Parent-${index}`} key={zone.id + selectedView} position={index} selected={false}>
          <IndexTable.Cell scope="col" id={index.toString()}>
            <Button variant="plain" onClick={() => handleShowDetailLocation(zone.id)}>
              {zone.name}
            </Button>
            <RegularText as="p">
              {zone.address?.address1}
              {!isEmpty(zone.address?.city) ? ', ' + zone.address?.city : ''}
              {!isEmpty(zone.address?.province) ? ', ' + zone.address?.province : ''}
              {!isEmpty(zone.address?.postalCode) ? ', ' + zone.address?.postalCode : ''}
              {!isEmpty(zone.address?.country) ? ', ' + zone.address?.country : ''}
            </RegularText>
          </IndexTable.Cell>
          <IndexTable.Cell>
            <Badge tone={'success'}>Enabled</Badge>
          </IndexTable.Cell>
          <IndexTable.Cell />
          <IndexTable.Cell>
            <RegularText alignment="start">
              <Button variant="plain" onClick={() => handleAddDeliverySettings(zone.id)} icon={NoteAddIcon}>
                Add delivery zone
              </Button>
            </RegularText>
          </IndexTable.Cell>
        </IndexTable.Row>
        {zone.settings
          .filter((item) => item.type === SETTING_TYPE.DELIVERY)
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
          .map((rowItem, rowIndex) => (
            <IndexTable.Row rowType="child" key={rowItem.id} id={rowIndex.toString()} position={rowIndex}>
              <IndexTable.Cell scope="row" headers={`${columnHeadings[0].id} ${index}`}>
                <Tooltip content={rowItem.name} width="wide" hoverDelay={300} dismissOnMouseOut>
                  <Box maxWidth="300px">
                    <RegularText truncate={true} breakWord={true}>
                      {rowItem.name}
                    </RegularText>
                  </Box>
                </Tooltip>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Badge tone={rowItem.isActive ? 'success' : undefined}>{rowItem.isActive ? 'Active' : 'Inactive'}</Badge>
              </IndexTable.Cell>
              <IndexTable.Cell />
              <IndexTable.Cell>
                <InlineStack align="start" gap={'400'}>
                  <Tooltip content="Edit setting">
                    <Button variant="plain" icon={EditIcon} onClick={() => handleEditDeliverySettings(Number(rowItem.id))} />
                  </Tooltip>
                  <Tooltip content="Duplicate">
                    <Button
                      variant="plain"
                      icon={DuplicateIcon}
                      onClick={() => handleShowModalDuplicateZone(Number(rowItem.id))}
                    />
                  </Tooltip>
                  <Tooltip content="Delete">
                    <Button
                      variant="plain"
                      tone="critical"
                      icon={DeleteIcon}
                      disabled={zone.settings.filter((item) => item.type === SETTING_TYPE.DELIVERY).length === 1}
                      onClick={() => {
                        setTypeDelete(TypeDelete.ZONE);
                        handleShowModalDeleteLocation(Number(rowItem.id));
                      }}
                    />
                  </Tooltip>
                </InlineStack>
              </IndexTable.Cell>
            </IndexTable.Row>
          ))}
      </Fragment>
    );
  });

  return rowMarkup;
};

export default TableDelivery;
